/**
 * Clubmonee Gravur (FEATURE-0001) — <clubmonee-engraving>
 *
 * Eigenes Custom Element, assets/theme.js wird NICHT veraendert.
 *
 * Aktivierung ist implizit (UX-Update 2026-07-06): Das Formular ist immer
 * sichtbar; die Gravur gilt als aktiv, sobald im AKTIVEN Modus Inhalt
 * vorhanden ist (Text nicht leer / Bild hochgeladen / Icon gewaehlt).
 * Leerer Zustand = normaler Kauf ohne Gravur und ohne Gebuehr-Produkt.
 *
 * Add-to-Cart-Strategie: theme.js (ProductForm, js/common/product/product-form.js)
 * registriert einen "submit"-Listener direkt auf dem Produktformular. Dieses Element
 * registriert einen Capture-Listener auf document: stopPropagation() in der
 * Capture-Phase verhindert, dass das Event das Formular (und damit theme.js)
 * erreicht. Bei aktiver Gravur wird der Submit durch EINEN fetch auf /cart/add.js
 * mit items-Array (Ring + Gebuehr-Produkt) ersetzt. Danach werden exakt die Events
 * gefeuert, die theme.js selbst nutzt (cart:prepare-bundled-sections, variant:add,
 * cart:change, theme:loading:*), damit Cart-Drawer/Cart-Count nativ rerendern.
 *
 * Alle sichtbaren Texte kommen als JSON aus dem Snippet (keine hartcodierten Strings).
 */

const DRAFT_KEY_PREFIX = 'clubmonee_engraving_draft:';
const UPLOAD_ENDPOINT_PATH = '/api/v1/uploads/';
// Obergrenze fuer einen Upload-Request (BUG-0001). Ohne Timeout bleiben die
// Kaufbuttons unbegrenzt gesperrt, wenn das Backend nicht antwortet.
const UPLOAD_TIMEOUT_MS = 60000;
const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png'];
const PREVIEW_MIN_FONT_SIZE = 6;
// Obergrenze der Einpassung. Seit der Ink-Messung (BUG-0002) begrenzt nicht
// mehr die zu klein geschaetzte Layout-Box die Groesse, sondern die
// tatsaechliche Glyphenflaeche — die Gravur darf die Steinflaeche deshalb
// ausfuellen. Der Wert ist nur noch ein Sicherheitsanschlag gegen absurde
// Groessen bei sehr grossen Vorschauen.
const PREVIEW_MAX_FONT_SIZE = 200;

// Referenzgroesse der Canvas-Messung (BUG-0002). Ink-Box, Advance und die
// em-basierten Design-Offsets skalieren alle linear mit der Schriftgroesse —
// eine Messung genuegt, die eingepasste Groesse ergibt sich als Dreisatz.
const PREVIEW_INK_REF_SIZE = 100;

// Reserve fuer das Gravur-Relief (--cm-engraving-relief, overrides.css): das
// drop-shadow liegt AUSSERHALB der Glyphen-Ink (1px oben, 1px Versatz + 1px
// Blur unten) und wuerde an der overflow-Kante der Form abgeschnitten. 2px
// allseitig deckt beide Schatten ab und puffert zugleich die Sub-Pixel-
// Divergenz zwischen Canvas-Advance und den gerundeten Layout-Breiten.
const PREVIEW_INK_RELIEF_PAD = 2;

// Zugabe auf den Farbverlauf-Rahmen je Seite (em): Relief-Schatten plus
// Rundungsdifferenz zwischen Canvas-Messung und gerendertem Layout.
const PREVIEW_INK_PAD_SAFETY_EM = 0.04;
// Abstand zwischen Schriftzug und Blaetterpfeil-Zone im Versatz-Design-Pfad
// (FEATURE-0003, Smoke-Befund T4), zusaetzlich zum halben Button-Durchmesser.
const PREVIEW_DESIGN_BUTTON_GAP = 4;
const MODES = ['none', 'text', 'image', 'svg'];
const ARTICLE_MODES = ['text', 'image', 'svg'];

/**
 * Design-Register (FEATURE-0003): kuratierte Initialen-Designs fuer den
 * Text-Modus. Offsets in em relativ zur eingepassten Schriftgroesse,
 * je massgeblicher Zeichenanzahl (2 und 3) ein Array; negativ = hochgestellt,
 * positiv = tiefgestellt, 0 = Grundlinie.
 *
 * FERTIGUNGS-REFERENZ (Entscheidung #11): Die Bestellung traegt nur den
 * Design-Namen (Property "Design") und den technischen Schluessel
 * (Property "_Design Key") — die exakten Versatz-Werte sind HIER dokumentiert
 * und massgeblich fuer die Gravur-Fertigung.
 *
 * ERWEITERUNGS-HINWEIS: Asymmetrische Offsets (z. B. { 2: [-0.5, 0] }) sind
 * seit BUG-0002 zulaessig. Die Einpassung misst die Ink-Union inklusive der
 * Versaetze (#measureInk) und zentriert sie anschliessend in der
 * Gravurflaeche (#applyInkShift) — die Lage wird also mitgeprueft, nicht nur
 * die Groesse. Die frueher noetige Symmetrie-Annahme entfaellt damit.
 */
const DESIGNS = [
  { key: 'standard', offsets: null },
  { key: 'raisedFirst', offsets: { 2: [-0.25, 0.25], 3: [-0.25, 0, 0.25] } },
  { key: 'loweredFirst', offsets: { 2: [0.25, -0.25], 3: [0.25, 0, -0.25] } },
];
const DESIGN_KEYS = DESIGNS.map((design) => design.key);
const DEFAULT_DESIGN_KEY = 'standard';

const readJsonScript = (root, selector) => {
  const script = root.querySelector(selector);

  if (!script) {
    return {};
  }

  try {
    return JSON.parse(script.textContent);
  } catch {
    return {};
  }
};

const safeLocalStorage = {
  get(key) {
    try {
      const raw = window.localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  },

  set(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* Speicher voll / Private Mode — Entwurf ist optional */
    }
  },

  remove(key) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* noop */
    }
  },
};

const generateGravurId = () =>
  window.crypto?.randomUUID?.() ?? `cm-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

/**
 * Zentrale Text-Normalisierung (Review R1, Unicode-Konsistenz): NFC faltet
 * dekomponierte Zeichen (z. B. "A" + kombinierendes Trema aus Copy-Paste)
 * zu einem Codepunkt. Wird an ALLEN Stellen angewendet, die state.text
 * setzen — Zaehlung (#designCharCount), Vorschau (#renderPreviewChars) und
 * Property-Werte arbeiten dadurch auf derselben normalisierten Basis.
 */
const normalizeEngravingText = (value) => (typeof value === 'string' ? value.normalize('NFC') : '');

/* ------------------------------------------------------------------ */
/* Ink-Messung der Vorschau (BUG-0002)                                 */
/* ------------------------------------------------------------------ */

/**
 * Ein einziger, wiederverwendeter 2D-Kontext. Es wird nie gezeichnet, nur
 * gemessen — measureText loest kein Layout aus (anders als der fruehere
 * Weg ueber scrollWidth/getBoundingClientRect in der Binaersuche).
 * undefined = noch nicht versucht, null = nicht verfuegbar.
 */
let previewInkCtx;

const inkContext = () => {
  if (previewInkCtx === undefined) {
    try {
      previewInkCtx = document.createElement('canvas').getContext('2d') ?? null;
    } catch {
      previewInkCtx = null;
    }
  }

  return previewInkCtx;
};

/** Erste Family einer computed font-family-Liste, ohne Quotes. */
const primaryFamily = (fontFamily) => (fontFamily.split(',')[0] ?? '').trim().replace(/^['"]|['"]$/g, '');

/**
 * em-Verhaeltnis einer computed Laenge zur aktuellen Schriftgroesse. Noetig,
 * weil bei der Referenzgroesse gemessen wird: geerbte em-Werte (vor allem
 * letter-spacing aus dem Theme) muessen dort neu ausgerechnet werden.
 */
const emRatio = (value, fontSizePx) => {
  const px = parseFloat(value);
  return Number.isFinite(px) && fontSizePx > 0 ? px / fontSizePx : 0;
};

/**
 * Text so aufbereiten, wie das Rendering ihn zeigt. Das Theme vererbt
 * text-transform auf den Vorschautext (css-variables.liquid); ein Preset mit
 * 'uppercase' wuerde die Canvas-Messung sonst still gegen den Rohstring
 * laufen lassen, waehrend das DOM Grossbuchstaben zeigt.
 */
const applyTextTransform = (text, transform) => {
  if (transform === 'uppercase') {
    return text.toUpperCase();
  }

  if (transform === 'lowercase') {
    return text.toLowerCase();
  }

  if (transform === 'capitalize') {
    return text.replace(/(^|\s)(\S)/gu, (match, space, char) => space + char.toUpperCase());
  }

  return text;
};

class ClubmoneeEngraving extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.i18n = readJsonScript(this, '[data-clubmonee-i18n]');
    this.productInfo = readJsonScript(this, '[data-clubmonee-product]');

    // Verknuepfte Gravurartikel je Modus (FEATURE-0002): { text: [...], svg: [...], image: [...] }.
    // Normalisiert auf { id: Number, title, priceFormatted, ink } — die id ist die zu
    // addierende Variant-ID, ink steuert die Farbe der Vorschau (FEATURE-0008).
    const rawArticles = readJsonScript(this, '[data-clubmonee-articles]');
    this.articles = {};

    for (const mode of ARTICLE_MODES) {
      const list = Array.isArray(rawArticles?.[mode]) ? rawArticles[mode] : [];
      this.articles[mode] = list
        .filter((article) => article && article.id != null)
        .map((article) => ({
          id: Number(article.id),
          title: article.title ?? '',
          priceFormatted: article.priceFormatted ?? '',
          ink: article.ink === 'alloy' ? 'alloy' : 'stone',
        }));
    }

    // Dropdowns der Artikel-Picker werden von #buildArticlePickers dynamisch erzeugt (siehe unten).
    this.articlePickerSelects = [];
    // Stabile ID für die Element-IDs der dynamischen Picker (label/for-Paarung).
    this.uid = this.getAttribute('form') || generateGravurId();

    this.refs = {
      modeRadios: Array.from(this.querySelectorAll('[data-clubmonee-mode]')),
      layout: this.querySelector('[data-clubmonee-layout]'),
      priceBadge: this.querySelector('[data-clubmonee-price-badge]'),
      textPanel: this.querySelector('[data-clubmonee-panel="text"]'),
      imagePanel: this.querySelector('[data-clubmonee-panel="image"]'),
      svgPanel: this.querySelector('[data-clubmonee-panel="svg"]'),
      textInput: this.querySelector('[data-clubmonee-text]'),
      counter: this.querySelector('[data-clubmonee-counter]'),
      fontSelect: this.querySelector('[data-clubmonee-font]'),
      designPicker: this.querySelector('[data-clubmonee-design-picker]'),
      designPrev: this.querySelector('[data-clubmonee-design-prev]'),
      designNext: this.querySelector('[data-clubmonee-design-next]'),
      designName: this.querySelector('[data-clubmonee-design-name]'),
      fileInput: this.querySelector('[data-clubmonee-file]'),
      fileButton: this.querySelector('[data-clubmonee-file-button]'),
      iconButtons: Array.from(this.querySelectorAll('[data-clubmonee-icon]')),
      error: this.querySelector('[data-clubmonee-error]'),
      customRequest: this.querySelector('[data-clubmonee-custom-request]'),
      previewShape: this.querySelector('[data-clubmonee-preview-shape]'),
      previewGhost: this.querySelector('[data-clubmonee-preview-ghost]'),
      previewText: this.querySelector('[data-clubmonee-preview-text]'),
      previewImage: this.querySelector('[data-clubmonee-preview-image]'),
      previewIcon: this.querySelector('[data-clubmonee-preview-icon]'),
      previewPlaceholder: this.querySelector('[data-clubmonee-preview-placeholder]'),
      previewLoader: this.querySelector('[data-clubmonee-preview-loader]'),
      status: this.querySelector('[data-clubmonee-status]'),
    };

    this.state = {
      mode: 'none',
      text: '',
      font: this.refs.fontSelect?.options[0]?.value ?? '',
      // Interne Design-Wahl (FEATURE-0003): bleibt auch ausserhalb des
      // 2-3-Zeichen-Fensters erhalten; wirksam wird sie nur ueber
      // #effectiveDesignKey() (Entscheidung #12).
      designKey: DEFAULT_DESIGN_KEY,
      imageUrl: null,
      uploadId: null,
      fileName: null,
      iconKey: null,
      // Pro Modus gewaehlte Gravurartikel-Variant-ID; Default: erster verfuegbarer Artikel.
      articleByMode: this.#defaultArticleByMode(),
      clientGravurId: null,
      uploading: false,
    };

    // Aktuell gewaehlte Legierung (FEATURE-0008). Startwert aus dem Variant-Picker,
    // der seine Variante als JSON mitliefert; danach gepflegt ueber variant:change.
    this.currentAlloy = this.#alloyFromVariant(
      document.querySelector('variant-picker')?.selectedVariant
    );

    this.submitting = false;

    // No-JS-/Race-Schutz: Alle Eingabefelder starten disabled und werden erst
    // freigeschaltet, wenn der Capture-Listener unten registriert ist. Ohne JS
    // kann so nie Gravur-Inhalt entstehen, den der native Submit ignorieren wuerde.
    const lockedControls = [
      ...this.refs.modeRadios,
      this.refs.textInput,
      this.refs.fontSelect,
      this.refs.designPrev,
      this.refs.designNext,
      this.refs.fileInput,
      ...this.refs.iconButtons,
    ];

    for (const control of lockedControls) {
      control?.removeAttribute('disabled');
    }

    for (const radio of this.refs.modeRadios) {
      radio.addEventListener('change', () => this.#onModeChange(radio.value), { signal });
    }

    // Artikel-Picker aus data-clubmonee-articles (Single Source of Truth) rendern.
    this.#buildArticlePickers(signal);

    this.refs.textInput?.addEventListener('input', () => this.#onTextInput(), { signal });
    this.refs.fontSelect?.addEventListener('change', () => this.#onFontChange(), { signal });
    this.refs.designPrev?.addEventListener('click', () => this.#onDesignStep(-1), { signal });
    this.refs.designNext?.addEventListener('click', () => this.#onDesignStep(1), { signal });
    this.refs.fileInput?.addEventListener('change', () => this.#onFileChange(), { signal });

    for (const button of this.refs.iconButtons) {
      button.addEventListener('click', () => this.#onIconClick(button), { signal });
    }

    this.refs.fileButton?.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.refs.fileInput?.click();
        }
      },
      { signal }
    );

    // Im Bild-Modus oeffnet die Vorschau selbst die Dateiauswahl
    // (Maus + Tastatur; role/tabindex verwaltet #render pro Modus)
    this.refs.previewShape?.addEventListener('click', () => this.#onPreviewActivate(), { signal });

    this.refs.previewShape?.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.#onPreviewActivate();
        }
      },
      { signal }
    );

    // Capture-Phase auf document: laeuft VOR dem submit-Listener von theme.js (ProductForm)
    document.addEventListener('submit', (event) => this.#onDocumentSubmit(event), { capture: true, signal });

    // Beim Variantenwechsel haengen zwei Dinge an der neuen Variante: die SKU im
    // Betreff des Gravurwunsch-Links und (FEATURE-0008) die Legierung, aus der
    // sich die Gravurfarbe ergibt. variant:change ist dasselbe Event, das
    // theme.js selbst verteilt (detail.variant).
    this.#productForm()?.addEventListener(
      'variant:change',
      (event) => {
        const variant = event.detail?.variant;

        this.#updateCustomRequestLink(variant?.sku);
        this.currentAlloy = this.#alloyFromVariant(variant);
        this.#applyInkColor();
      },
      { signal }
    );

    if (window.ResizeObserver && this.refs.previewShape) {
      // Kurzschluss bei unveraenderter Groesse: synchrones Style-Setzen im
      // Callback provoziert sonst "ResizeObserver loop completed with
      // undelivered notifications".
      this.resizeObserver = new ResizeObserver(([entry]) => {
        const { inlineSize, blockSize } = entry?.contentBoxSize?.[0] ?? {};

        if (inlineSize === this.lastShapeWidth && blockSize === this.lastShapeHeight) {
          return;
        }

        this.lastShapeWidth = inlineSize;
        this.lastShapeHeight = blockSize;
        this.#fitVisiblePreviewSpan();
      });
      this.resizeObserver.observe(this.refs.previewShape);
    }

    // Auffangnetz fuer die Ink-Messung (BUG-0002): Ghost-Initialen und
    // wiederhergestellte Entwuerfe laufen nicht zwingend durch einen
    // fonts.load-Aufruf, und font-display: swap tauscht die Schrift
    // unabhaengig davon aus.
    document.fonts?.addEventListener?.('loadingdone', () => this.#fitVisiblePreviewSpan(), { signal });

    this.#restoreDraft();
    this.#render();
  }

  disconnectedCallback() {
    this.abortController.abort();
    this.resizeObserver?.disconnect();
  }

  /* ------------------------------------------------------------------ */
  /* Aktivierungslogik (implizit)                                        */
  /* ------------------------------------------------------------------ */

  /**
   * Gravur ist aktiv, wenn im AKTIVEN Modus Inhalt vorhanden ist.
   * Ghost-Initialen der Vorschau zaehlen nie als Inhalt.
   */
  #hasEngravingContent() {
    if (this.state.mode === 'text') {
      return this.state.text.trim() !== '';
    }

    if (this.state.mode === 'image') {
      return Boolean(this.state.uploadId && this.state.imageUrl);
    }

    if (this.state.mode === 'svg') {
      return Boolean(this.state.iconKey);
    }

    // 'none': neutraler Ausgangszustand — Inhalte anderer Modi bleiben im
    // Hintergrund erhalten, zaehlen aber nicht (normaler Kauf ohne Gebuehr)
    return false;
  }

  /* ------------------------------------------------------------------ */
  /* UI-Events                                                           */
  /* ------------------------------------------------------------------ */

  #onModeChange(mode) {
    this.state.mode = MODES.includes(mode) ? mode : 'none';
    this.#clearError();
    this.#render();
    this.#saveDraft();

    // Autofokus nur bei echter User-Interaktion (change-Event), nie beim
    // localStorage-Restore — sonst scrollt die Seite beim Laden zum Block
    if (this.state.mode === 'text') {
      this.refs.textInput?.focus();
    }
  }

  /** Auswahl eines Gravurartikels innerhalb eines Modus (nur bei mehreren Artikeln sichtbar) */
  #onArticleChange(select) {
    const mode = select.dataset.clubmoneeArticle;
    const id = Number(select.value);

    if (ARTICLE_MODES.includes(mode) && id) {
      this.state.articleByMode[mode] = id;
      this.#render();
      this.#saveDraft();
    }
  }

  /**
   * Beschriftung einer Dropdown-Option: Titel plus Aufpreis, damit der Preisunterschied
   * auch im zugeklappten Zustand sichtbar ist (das Badge zeigt immer nur die aktive Wahl).
   * priceFormatted ist in Liquid via money vorformatiert — das JS formatiert nie selbst.
   */
  #articleOptionLabel(article) {
    return article.priceFormatted ? `${article.title} (+ ${article.priceFormatted})` : article.title;
  }

  /**
   * Rendert je Modus das Artikel-Dropdown aus this.articles[mode] in den Liquid-Mount
   * (data-clubmonee-article-mount), geklont aus data-clubmonee-article-template. Kein
   * Picker bei 0 oder genau 1 Artikel (der einzige Artikel wird automatisch verwendet).
   * data-clubmonee-articles ist die einzige Datenquelle — keine doppelte Metafeld-Auflösung
   * in Liquid; das Markup wiederum kommt ausschliesslich aus der Liquid-Vorlage.
   */
  #buildArticlePickers(signal) {
    this.articlePickerSelects = [];

    const template = this.querySelector('[data-clubmonee-article-template]');

    for (const mode of ARTICLE_MODES) {
      const mount = this.querySelector(`[data-clubmonee-article-mount="${mode}"]`);

      if (!mount) {
        continue;
      }

      mount.replaceChildren();

      const list = this.articles[mode] ?? [];

      if (!template || list.length <= 1) {
        continue;
      }

      const picker = template.content.cloneNode(true);
      const root = picker.querySelector('.clubmonee-engraving__articles');
      const label = picker.querySelector('[data-clubmonee-article-label]');
      const select = picker.querySelector('[data-clubmonee-article]');

      if (!root || !label || !select) {
        continue;
      }

      const selectId = `ClubmoneeEngravingArticle-${mode}-${this.uid}`;

      root.dataset.clubmoneeArticlePicker = mode;
      label.htmlFor = selectId;
      label.textContent = this.i18n.articleLabel ?? '';

      select.id = selectId;
      select.dataset.clubmoneeArticle = mode;

      for (const article of list) {
        const option = document.createElement('option');
        option.value = String(article.id);
        option.textContent = this.#articleOptionLabel(article);
        select.appendChild(option);
      }

      select.value = String(list[0].id);
      select.addEventListener('change', () => this.#onArticleChange(select), { signal });

      this.articlePickerSelects.push(select);
      mount.appendChild(picker);
    }
  }

  /**
   * Setzt jedes Artikel-Dropdown auf die im State wirksame Wahl. Ueber #selectedArticleId,
   * damit ein nicht mehr verknuepfter Artikel auf den ersten verfuegbaren zurueckfaellt und
   * das Dropdown nie ohne sichtbare Auswahl (selectedIndex -1) dasteht.
   */
  #syncArticlePickers() {
    for (const select of this.articlePickerSelects) {
      const id = this.#selectedArticleId(select.dataset.clubmoneeArticle);

      if (id != null) {
        select.value = String(id);
      }
    }
  }

  /** Standard-Auswahl je Modus: erster verfuegbarer Gravurartikel (Reihenfolge = Metafeld-Reihenfolge) */
  #defaultArticleByMode() {
    const map = {};

    for (const mode of ARTICLE_MODES) {
      map[mode] = this.articles?.[mode]?.[0]?.id ?? null;
    }

    return map;
  }

  /**
   * Aktuell wirksame Gravurartikel-Variant-ID fuer einen Modus. Faellt auf den
   * ersten verfuegbaren Artikel zurueck, wenn die gespeicherte Wahl nicht (mehr)
   * verknuepft ist (AK: Fallback bei nicht mehr vorhandenem Artikel).
   */
  #selectedArticleId(mode) {
    const list = this.articles?.[mode] ?? [];

    if (list.length === 0) {
      return null;
    }

    const chosen = this.state.articleByMode?.[mode];

    if (chosen && list.some((article) => article.id === chosen)) {
      return chosen;
    }

    return list[0].id;
  }

  #selectedArticle(mode) {
    const id = this.#selectedArticleId(mode);
    return this.articles?.[mode]?.find((article) => article.id === id) ?? null;
  }

  /* ------------------------------------------------------------------ */
  /* Materialfarben der Vorschau (FEATURE-0008)                          */
  /* ------------------------------------------------------------------ */

  /**
   * Liest den Legierungswert aus einem Varianten-Objekt.
   *
   * Welche der drei Optionen die Legierung ist, entscheidet Liquid und liefert
   * die Position als alloyOptionIndex mit. Fest verdrahtet waere das falsch:
   * nicht jedes Produkt fuehrt dieselbe Optionsreihenfolge.
   */
  #alloyFromVariant(variant) {
    const index = this.productInfo?.alloyOptionIndex;

    if (!variant || typeof index !== 'number' || index < 0) {
      return null;
    }

    return variant.options?.[index] ?? null;
  }

  /**
   * Setzt die Gravurfarbe auf der Vorschauflaeche.
   *
   * Hier wird nichts gerechnet: beide Werte kommen fertig aus Liquid, das JS
   * entscheidet nur, welcher gilt. Gravurartikel mit Goldfuellung (ink: alloy)
   * bekommen den Verlauf der gewaehlten Legierung, alle anderen den aus der
   * Steinfarbe abgeleiteten Ton-in-Ton-Wert.
   *
   * Reihenfolge der Rueckfaelle: Legierungsfarbe, dann Steinton, dann gar
   * nichts. Der letzte Fall loescht die Variable bewusst, statt eine beliebige
   * Farbe zu setzen — dann greift der Fallback im CSS und die Vorschau sieht
   * aus wie vor diesem Feature.
   */
  #applyInkColor() {
    const shape = this.refs.previewShape;

    if (!shape) {
      return;
    }

    const stoneInk = this.productInfo?.stoneInk || '';
    const article = this.state.mode === 'none' ? null : this.#selectedArticle(this.state.mode);
    let ink = stoneInk;

    if (article?.ink === 'alloy' && this.currentAlloy) {
      ink = this.productInfo?.alloyInk?.[this.currentAlloy] || stoneInk;
    }

    if (ink) {
      shape.style.setProperty('--cm-engraving-ink', ink);
    } else {
      shape.style.removeProperty('--cm-engraving-ink');
    }
  }

  /**
   * Aufpreis-Badge: zeigt den in Liquid (money) vorformatierten Preis des im
   * aktuellen Modus gewaehlten Gravurartikels. Im Modus 'none' ausgeblendet.
   * Das JS formatiert keine Waehrung selbst (AK Dynamischer Aufpreis).
   */
  #updatePriceBadge() {
    const badge = this.refs.priceBadge;

    if (!badge) {
      return;
    }

    const article = this.state.mode === 'none' ? null : this.#selectedArticle(this.state.mode);

    if (article) {
      badge.textContent = `+ ${article.priceFormatted}`;
      badge.hidden = false;
    } else {
      badge.textContent = '';
      badge.hidden = true;
    }
  }

  #onTextInput() {
    this.state.text = normalizeEngravingText(this.refs.textInput?.value);
    this.#clearError();
    this.#render();
    this.#saveDraft();
  }

  #onFontChange() {
    this.state.font = this.refs.fontSelect?.value ?? '';
    this.#render();
    this.#saveDraft();
  }

  /* ------------------------------------------------------------------ */
  /* Text-Designs (FEATURE-0003)                                         */
  /* ------------------------------------------------------------------ */

  /**
   * Massgebliche Zeichenanzahl: Gravurtext getrimmt und ohne Leerzeichen
   * ('A B' = 2, 'A.B.' = 4). Die Zaehlweise steht NUR hier (Entscheidung #2);
   * alle Verbraucher (#isDesignEligible, Offset-Zuordnung) nutzen diese Methode.
   * Gezaehlt wird in Code-Points ([...str], Review R1) — identisch zur
   * for...of-Segmentierung in #renderPreviewChars, damit z. B. 'A\u{1D505}'
   * als 2 zaehlt und 2 Spans erhaelt (state.text ist NFC-normalisiert).
   */
  #designCharCount() {
    return [...this.state.text.trim().replace(/\s+/g, '')].length;
  }

  /** Designs stehen nur bei 2-3 massgeblichen Zeichen zur Verfuegung. */
  #isDesignEligible() {
    const count = this.#designCharCount();
    return count >= 2 && count <= 3;
  }

  /**
   * Wirksames Design (Entscheidung #12): ausserhalb des 2-3-Zeichen-Fensters
   * still 'standard'; die interne Wahl (state.designKey) bleibt erhalten und
   * gilt bei Rueckkehr ins Fenster wieder. Vorschau, Namens-Anzeige und
   * Properties nutzen ausschliesslich diese Ableitung.
   */
  #effectiveDesignKey() {
    return this.#isDesignEligible() ? this.state.designKey : DEFAULT_DESIGN_KEY;
  }

  /** Lokalisierter Anzeigename eines Designs aus dem i18n-Block. */
  #designName(key) {
    return this.i18n.designs?.[key] ?? key;
  }

  /** Zyklisches Blaettern: nach dem letzten Design folgt wieder das erste. */
  #onDesignStep(step) {
    const index = DESIGN_KEYS.indexOf(this.state.designKey);
    const nextIndex = (Math.max(index, 0) + step + DESIGN_KEYS.length) % DESIGN_KEYS.length;
    this.state.designKey = DESIGN_KEYS[nextIndex];
    this.#render();
    this.#saveDraft();
  }

  /** Icon waehlen; erneuter Klick auf das gewaehlte Icon waehlt ab */
  #onIconClick(button) {
    const key = button.dataset.clubmoneeIcon;
    this.state.iconKey = this.state.iconKey === key ? null : key;
    this.#clearError();
    this.#render();
    this.#saveDraft();
  }

  async #onFileChange() {
    const file = this.refs.fileInput?.files?.[0];

    if (!file) {
      return;
    }

    this.#clearError();

    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      this.#showError('unsupported_format');
      this.refs.fileInput.value = '';
      return;
    }

    const maxFileBytes = this.#maxFileMb() * 1024 * 1024;

    if (file.size > maxFileBytes) {
      this.#showError('file_too_large');
      this.refs.fileInput.value = '';
      return;
    }

    await this.#uploadFile(file);
  }

  /** Klick/Enter/Space auf der Vorschau oeffnet im Bild-Modus die Dateiauswahl */
  #onPreviewActivate() {
    if (this.state.mode === 'image' && !this.state.uploading) {
      this.refs.fileInput?.click();
    }
  }

  /* ------------------------------------------------------------------ */
  /* Upload                                                              */
  /* ------------------------------------------------------------------ */

  async #uploadFile(file) {
    const baseUrl = (this.dataset.uploadUrl ?? '').trim();

    if (!baseUrl) {
      // Ohne Basis-URL ist der Bild-Modus funktionslos — im Fehlerfall (BUG-0001)
      // muss das in der Konsole zuordenbar sein statt nur als "generic" beim Kunden.
      console.error(
        '[clubmonee-engraving] Kein Upload-Ziel konfiguriert: Theme-Setting clubmonee_engraving_upload_url ist leer.'
      );
      this.#clearUploadedImage();
      this.#showError('generic');
      this.#render();
      return;
    }

    // Vor dem uploading-State aufloesen: ein unbrauchbarer Setting-Wert wuerfe hier,
    // und die Kaufbuttons blieben sonst dauerhaft gesperrt.
    let endpoint;

    try {
      endpoint = new URL(UPLOAD_ENDPOINT_PATH, baseUrl).toString();
    } catch (error) {
      console.error(
        '[clubmonee-engraving] Ungueltige Upload-URL im Theme-Setting clubmonee_engraving_upload_url:',
        baseUrl,
        error
      );
      this.#clearUploadedImage();
      this.#showError('generic');
      this.#render();
      return;
    }

    this.state.uploading = true;
    this.#setAddToCartDisabled(true);
    this.#render();

    let errorCode = null;

    try {
      const body = new FormData();
      body.append('file', file);

      const request = { method: 'POST', body };

      if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
        request.signal = AbortSignal.timeout(UPLOAD_TIMEOUT_MS);
      }

      const response = await fetch(endpoint, request);

      if (response.status === 201) {
        const payload = await response.json();
        this.state.uploadId = String(payload.id);
        this.state.imageUrl = String(payload.url);
        this.state.fileName = file.name;
      } else {
        errorCode = await this.#resolveErrorCode(response);
      }
    } catch (error) {
      // Geworfener fetch = Verbindungs-, Protokoll- oder Timeout-Fehler, also kein
      // Server-Contract-Fehler. Frueher landete das stumm auf "generic" — ein toter
      // Backend-Host blieb dadurch wochenlang unbemerkt (BUG-0001).
      console.error('[clubmonee-engraving] Upload fehlgeschlagen:', endpoint, error);
      errorCode = 'network';
    } finally {
      this.state.uploading = false;
      this.#setAddToCartDisabled(false);
    }

    if (errorCode) {
      // Ohne Reset bliebe ein zuvor erfolgreich hochgeladenes Bild aktiv: der Kunde
      // saehe die alte Vorschau plus Fehlermeldung und koennte das falsche Bild
      // bestellen (BUG-0001).
      this.#clearUploadedImage();
      this.#showError(errorCode);
    }

    this.#saveDraft();
    this.#render();
  }

  /** Hochgeladenes Bild samt Dateiauswahl verwerfen, ohne den Modus zu wechseln */
  #clearUploadedImage() {
    this.state.uploadId = null;
    this.state.imageUrl = null;
    this.state.fileName = null;

    if (this.refs.fileInput) {
      this.refs.fileInput.value = '';
    }
  }

  async #resolveErrorCode(response) {
    let code = null;

    try {
      const payload = await response.json();
      code = payload.code ?? payload.error?.code ?? (typeof payload.error === 'string' ? payload.error : null);
    } catch {
      /* kein JSON-Body */
    }

    if (code === 'file_required') {
      // Backend-Contract-Code ohne eigene Kunden-Meldung — praktisch nicht
      // erreichbar (das JS haengt 'file' immer an), daher generisch statt
      // irrefuehrend "Format nicht unterstuetzt" (Review-Finding 10)
      return 'generic';
    }

    if (code && this.i18n.errors?.[code]) {
      return code;
    }

    if (response.status === 413) {
      return 'file_too_large';
    }

    if (response.status === 400 || response.status === 415 || response.status === 422) {
      return 'unsupported_format';
    }

    if (response.status === 429) {
      return 'throttled';
    }

    return 'generic';
  }

  /* ------------------------------------------------------------------ */
  /* Add-to-Cart-Interception                                            */
  /* ------------------------------------------------------------------ */

  #onDocumentSubmit(event) {
    const form = this.#productForm();

    if (!form || event.target !== form) {
      return;
    }

    if (!this.#hasEngravingContent()) {
      // native Flow (theme.js): normaler Kauf ohne Gravur, ohne Gebuehr,
      // ohne Properties — identische Varianten mergen normal (AK 44)
      return;
    }

    // theme.js darf diesen Submit nicht zusaetzlich verarbeiten
    event.preventDefault();
    event.stopPropagation();

    this.#addToCart(form, event.submitter);
  }

  async #addToCart(form, submitter) {
    // Re-Entry-Guard unabhaengig vom submitter (Review-Finding 5):
    // bei form.requestSubmit() ohne Button ist event.submitter null.
    if (this.submitting || this.state.uploading || submitter?.getAttribute('aria-busy') === 'true') {
      return;
    }

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    // Ohne Property-Keys aus dem i18n-Block laut ausfallen statt still
    // falschsprachige Keys in Bestellungen zu schreiben (Review-Finding 9).
    const propertyKeys = this.i18n.properties;

    if (!propertyKeys?.for || !propertyKeys?.text || !propertyKeys?.font || !propertyKeys?.icon || !propertyKeys?.design) {
      console.error('[clubmonee-engraving] i18n property keys missing — engraving add-to-cart aborted.');
      return;
    }

    // Haupt-Item aus der echten FormData ableiten (Review-Finding 4), damit
    // Properties anderer Bloecke (native line_item_property, Gift-Card-
    // Recipient, ...) und selling_plan nicht verloren gehen — theme.js
    // wuerde die komplette FormData senden.
    const formData = new FormData(form);
    const variantId = Number(formData.get('id'));

    // Line-Item-Variant-ID kommt aus dem gewaehlten Gravurartikel des aktiven Modus
    // (FEATURE-0002), nicht mehr aus einem festen Gebuehr-Produkt.
    const articleVariantId = this.#selectedArticleId(this.state.mode);

    if (!variantId || !articleVariantId) {
      return;
    }

    const quantity = Math.max(1, Number(formData.get('quantity')) || 1);

    // _Gravur ID: Bild-Modus = Upload-UUID; Text-/Icon-Modus = clientseitige ID
    if (this.state.mode !== 'image' && !this.state.clientGravurId) {
      this.state.clientGravurId = generateGravurId();
      this.#saveDraft();
    }

    const gravurId = this.state.mode === 'image' ? this.state.uploadId : this.state.clientGravurId;

    const mainProperties = {};

    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);

      if (match && typeof value === 'string') {
        mainProperties[match[1]] = value;
      }
    }

    mainProperties['_Gravur ID'] = gravurId;

    const mainItem = { id: variantId, quantity, properties: mainProperties };
    const sellingPlan = formData.get('selling_plan');

    if (sellingPlan) {
      mainItem.selling_plan = Number(sellingPlan);
    }

    const feeProperties = {
      [propertyKeys.for]: this.#forPropertyValue(variantId),
      '_Gravur ID': gravurId,
      // Marker: kennzeichnet diese Zeile eindeutig als Gravurartikel-Zeile. Noetig,
      // weil Ring und Gravurartikel dieselbe _Gravur ID teilen (Warenkorb-Paarung).
      '_Gravur Artikel': '1',
    };

    if (this.state.mode === 'text') {
      feeProperties[propertyKeys.text] = this.state.text.trim();
      feeProperties[propertyKeys.font] = this.state.font;
      // Design (FEATURE-0003): WIRKSAMES Design, Muster Symbol + _Symbol Key —
      // sichtbarer lokalisierter Name + versteckter technischer Key, im
      // Text-Modus IMMER geschrieben, auch bei 'standard' (Entscheidung #6).
      const effectiveDesignKey = this.#effectiveDesignKey();
      feeProperties[propertyKeys.design] = this.#designName(effectiveDesignKey);
      feeProperties['_Design Key'] = effectiveDesignKey;
    } else if (this.state.mode === 'image') {
      // Versteckt (Underscore-Prefix): die Backend-URL soll weder im Checkout noch
      // in den Bestell-Mails als nackter Link erscheinen. Im Admin-Bestelldetail
      // bleibt sie fuer die Fertigung sichtbar, im Warenkorb rendert
      // snippets/line-item.liquid daraus das Thumbnail. Bewusst hart kodiert und
      // nicht lokalisiert (wie _Gravur ID / _Design Key / _Symbol Key), damit der
      // Key fuer die Fertigung sprachunabhaengig stabil bleibt.
      feeProperties['_Gravur Bild'] = this.state.imageUrl;
      feeProperties['_Gravur Upload ID'] = this.state.uploadId;
    } else if (this.state.mode === 'svg') {
      const selectedButton = this.refs.iconButtons.find((button) => button.dataset.clubmoneeIcon === this.state.iconKey);
      feeProperties[propertyKeys.icon] = selectedButton?.dataset.iconName ?? this.state.iconKey;
      feeProperties['_Symbol Key'] = this.state.iconKey;
    }

    const items = [mainItem, { id: articleVariantId, quantity, properties: feeProperties }];

    this.submitting = true;
    const routesRoot = window.Shopify?.routes?.root ?? '/';
    const submitButtons = this.#submitButtons(form);
    const showLoadingBar = submitter?.querySelector('button-content') === null;
    const sectionsToBundle = new Set();

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    for (const button of submitButtons) {
      button.setAttribute('aria-busy', 'true');
    }

    if (showLoadingBar) {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
    }

    let response = null;
    let responseJson = null;

    try {
      response = await fetch(`${routesRoot}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest', // wie theme.js — noetig fuer Inventory-Check
        },
        body: JSON.stringify({ items, sections: [...sectionsToBundle].join(',') }),
      });
      responseJson = await response.json();
    } catch {
      response = null;
    } finally {
      this.submitting = false;

      for (const button of submitButtons) {
        button.removeAttribute('aria-busy');
      }

      if (showLoadingBar) {
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      }
    }

    if (response?.ok) {
      this.#clearDraft(); // AK 61
      this.#resetAfterAdd();

      const themeSettings = window.themeVariables?.settings ?? {};

      if (themeSettings.cartType === 'page' || themeSettings.pageType === 'cart') {
        window.location.href = `${routesRoot}cart`;
        return;
      }

      const cartContent = await (await fetch(`${routesRoot}cart.js`)).json();
      cartContent['sections'] = responseJson['sections'];
      const addedItems = Object.hasOwn(responseJson, 'items') ? responseJson['items'] : [responseJson];

      form.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: addedItems, cart: cartContent } }));

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart: cartContent } })
      );

      if (themeSettings.cartType === 'message') {
        document.dispatchEvent(
          new CustomEvent('toast:show', {
            detail: {
              message: (window.themeVariables?.strings?.addedToCart ?? '').replace(
                '{{product_title}}',
                addedItems?.[0]?.title ?? ''
              ),
            },
          })
        );
      } else if (themeSettings.cartType === 'drawer') {
        // Screenreader-Ansage analog theme.js announceStatus (Review-Finding 7)
        const template = window.themeVariables?.strings?.addedToCart ?? '';
        const announcement = addedItems
          .map((item) => template.replace('{{product_title}}', item?.title ?? ''))
          .join(', ');

        if (this.refs.status) {
          this.refs.status.textContent = announcement;
        }
      }
    } else {
      const message = responseJson?.message ?? this.i18n.errors?.generic ?? '';

      form.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: message } }));
      document.dispatchEvent(new CustomEvent('toast:show', { detail: { message, tone: 'error' } }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }
  }

  #forPropertyValue(variantId) {
    const title = this.productInfo.title ?? '';

    if (this.productInfo.hasOnlyDefaultVariant) {
      return title;
    }

    const variantTitle = this.productInfo.variantTitles?.[String(variantId)];
    return variantTitle ? `${title} – ${variantTitle}` : title;
  }

  /* ------------------------------------------------------------------ */
  /* Rendering                                                           */
  /* ------------------------------------------------------------------ */

  #render() {
    const { mode } = this.state;

    // 'Ohne Gravur': Formularbereich + Vorschau komplett ausgeblendet,
    // Preis-Badge nur bei gewaehltem Gravur-Modus
    if (this.refs.layout) {
      this.refs.layout.hidden = mode === 'none';
    }

    this.#updatePriceBadge();
    this.#applyInkColor();

    if (this.refs.textPanel) {
      this.refs.textPanel.hidden = mode !== 'text';
    }

    if (this.refs.imagePanel) {
      this.refs.imagePanel.hidden = mode !== 'image';
    }

    if (this.refs.svgPanel) {
      this.refs.svgPanel.hidden = mode !== 'svg';
    }

    if (this.refs.counter) {
      const template = this.i18n.charCounter ?? '[current]/[max]';
      this.refs.counter.textContent = template
        .replace('[current]', String(this.state.text.length))
        .replace('[max]', String(this.#maxChars()));
    }

    // Design-Auswahl (FEATURE-0003): ausserhalb des 2-3-Zeichen-Fensters
    // KOMPLETT ausgeblendet (Entscheidung #5, hidden-Attribut — ausgeblendete
    // Buttons sind automatisch nicht fokussierbar). Da die Overlay-Pfeile auf
    // der von allen Modi geteilten Vorschau liegen (Entscheidung #14), blendet
    // zusaetzlich jeder Nicht-Text-Modus (image/svg/none) aus. Die
    // Namens-Anzeige zeigt immer das WIRKSAME Design.
    if (this.refs.designPicker) {
      this.refs.designPicker.hidden = mode !== 'text' || !this.#isDesignEligible();
    }

    if (this.refs.designName) {
      this.refs.designName.textContent = this.#designName(this.#effectiveDesignKey());
    }

    // Ausgewaehltes Icon markieren (block-swatch selected-Optik + aria-pressed)
    for (const button of this.refs.iconButtons) {
      const selected = button.dataset.clubmoneeIcon === this.state.iconKey;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
    }

    // Im Bild-Modus ist die Vorschau selbst klick- und tastaturbedienbar
    // (oeffnet die Dateiauswahl), in den anderen Modi ist sie rein dekorativ
    const shape = this.refs.previewShape;

    if (shape) {
      if (mode === 'image') {
        shape.setAttribute('role', 'button');
        shape.setAttribute('tabindex', '0');
        shape.setAttribute('data-clickable', '');
        shape.setAttribute('aria-label', (this.state.uploadId ? this.i18n.chooseOtherFile : this.i18n.chooseFile) ?? '');
      } else {
        shape.removeAttribute('role');
        shape.removeAttribute('tabindex');
        shape.removeAttribute('data-clickable');
        shape.removeAttribute('aria-label');
      }
    }

    if (this.refs.fileButton) {
      const label = this.state.uploadId ? this.i18n.chooseOtherFile : this.i18n.chooseFile;
      const labelTarget = this.refs.fileButton.querySelector('[data-clubmonee-file-button-label]') ?? this.refs.fileButton;

      if (label) {
        labelTarget.textContent = label;
      }
    }

    this.#renderPreview();
    this.#updateBypassButtons();
  }

  #renderPreview() {
    const { mode, text, imageUrl, uploading } = this.state;
    const { previewGhost, previewText, previewImage, previewIcon, previewPlaceholder, previewLoader, fontSelect } = this.refs;

    if (previewLoader) {
      previewLoader.hidden = !uploading;
    }

    const showText = mode === 'text' && text !== '';
    const showGhost = mode === 'text' && text === '';
    const showImage = mode === 'image' && Boolean(imageUrl) && !uploading;
    const showImagePlaceholder = mode === 'image' && !imageUrl && !uploading;
    const showIcon = mode === 'svg' && Boolean(this.state.iconKey);

    const selectedOption = fontSelect?.selectedOptions?.[0];
    const family = (selectedOption?.dataset.family ?? '').replace(/'/g, '');

    if (previewText) {
      previewText.hidden = !showText;

      if (showText) {
        this.#renderPreviewChars(previewText, text);
        this.#applyPreviewFont(previewText, family);
      } else {
        // Sonst bliebe die Verschiebung der letzten Eingabe stehen und der
        // Schriftzug blitzte beim naechsten Einblenden versetzt auf.
        this.#clearInkShift(previewText);
        this.#clearInkPadding(previewText);
      }
    }

    // Ghost-Initialen: nur Anzeige, nie Property, zaehlt nicht als Inhalt
    if (previewGhost) {
      previewGhost.hidden = !showGhost;

      if (showGhost) {
        this.#applyPreviewFont(previewGhost, family);
      } else {
        this.#clearInkShift(previewGhost);
        this.#clearInkPadding(previewGhost);
      }
    }

    if (previewImage) {
      if (showImage) {
        previewImage.src = imageUrl;
        previewImage.hidden = false;
      } else {
        previewImage.hidden = true;
        previewImage.removeAttribute('src');
      }
    }

    if (previewIcon) {
      previewIcon.hidden = !showIcon;

      if (showIcon) {
        // Das Motiv wird als CSS-Maske gesetzt und mit der Materialfarbe
        // hinterlegt (FEATURE-0008), statt das Inline-SVG des Swatch-Buttons zu
        // klonen. Ein geklontes SVG liesse sich nur einfarbig ueber currentColor
        // fuellen; ein Verlauf, wie ihn die Legierungen mitbringen, geht darueber
        // nicht. Ueber die Maske faerbt derselbe Codepfad Text und Symbol gleich.
        const selectedButton = this.refs.iconButtons.find(
          (button) => button.dataset.clubmoneeIcon === this.state.iconKey
        );
        const url = selectedButton?.dataset.clubmoneeIconUrl;

        previewIcon.replaceChildren();
        this.#applyPreviewMask(previewIcon, url);

        // Ohne Asset-URL gaebe es keine Maske und damit eine volle Farbflaeche
        // in der Vorschau. Dann lieber nichts zeigen.
        previewIcon.hidden = !url;
      } else {
        previewIcon.replaceChildren();
        this.#applyPreviewMask(previewIcon, null);
      }
    }

    if (previewPlaceholder) {
      previewPlaceholder.hidden = !showImagePlaceholder;
    }
  }

  /**
   * Setzt oder loescht die Maske der Symbol-Vorschau.
   *
   * Der Pfad geht als Custom Property --cm-icon-mask ans Element, nicht direkt
   * als mask-image: die Maske wirkt im CSS auf dem Pseudo-Element, damit das
   * Gravur-Relief auf dem Elternelement nicht von ihr weggeschnitten wird
   * (CSS wendet filter vor mask an).
   */
  #applyPreviewMask(element, url) {
    if (!url) {
      element.style.removeProperty('--cm-icon-mask');
      return;
    }

    element.style.setProperty('--cm-icon-mask', `url("${url}")`);
  }

  /**
   * Zerlegt den Gravurtext in Zeichen-Spans (FEATURE-0003) und setzt je
   * Zeichen den em-relativen Versatz des wirksamen Designs als CSS Custom
   * Property (--cm-offset → transform: translateY im CSS, skaliert also mit
   * der eingepassten Schriftgroesse). Leerzeichen zaehlen nicht zur
   * massgeblichen Zeichenanzahl und bleiben auf der Grundlinie; die Offsets
   * gelten fuer die Nicht-Leerzeichen-Zeichen in Reihenfolge. Im Design
   * 'standard' (offsets null) entsteht dasselbe Grundlinien-Layout wie vor
   * dem Feature (keine Regression), die Ghost-Initialen bleiben unveraendert
   * ein einzelner Text-Node (immer Standard-Layout, Entscheidung #13).
   */
  #renderPreviewChars(span, text) {
    const design = DESIGNS.find((entry) => entry.key === this.#effectiveDesignKey());
    const offsets = design?.offsets?.[this.#designCharCount()] ?? null;

    // Ohne Offsets (wirksames Design 'standard') bleibt der Text ein einzelner
    // Text-Node: exakt das Layout von vor dem Feature inkl. Kerning/Ligaturen
    // der Script-Fonts, die Zeichen-Spans unterbrechen wuerden (keine Regression).
    if (!offsets) {
      span.textContent = text;
      return;
    }

    span.replaceChildren();

    let offsetIndex = 0;

    for (const char of text) {
      const charSpan = document.createElement('span');
      charSpan.className = 'clubmonee-engraving__preview-char';
      charSpan.dataset.clubmoneeDesignChar = '';
      charSpan.textContent = char;

      if (!/\s/.test(char)) {
        const offset = offsets[offsetIndex] ?? 0;

        if (offset !== 0) {
          charSpan.style.setProperty('--cm-offset', `${offset}em`);
        }

        offsetIndex += 1;
      }

      span.appendChild(charSpan);
    }
  }

  /** Setzt die Font-Family und passt die Groesse nach dem Font-Load erneut ein */
  #applyPreviewFont(span, family) {
    if (family) {
      span.style.fontFamily = `'${family}'`;

      // Immer anfordern und danach neu einpassen, ohne Vorpruefung per
      // document.fonts.check: WebKit meldet dort true, sobald die Familie
      // deklariert ist, auch wenn die Datei noch nicht geladen ist. Die
      // Ink-Messung lief dann gegen die Ersatzschrift (deren Glyphen kaum
      // ueber ihre Advance hinausragen) und passte "TF" in Great Vibes auf
      // 187px statt 91px ein — ohne Korrektur, weil ein uebersprungener
      // load()-Aufruf auch kein Nachmessen ausloest.
      //
      // Mit dem konkreten Text anfordern: bei unicode-range-Subsets laedt der
      // Browser sonst nur die Default-Glyphen. Ein bereits geladener Font
      // loest sofort auf, der Aufruf kostet dann nichts.
      if (document.fonts?.load) {
        document.fonts
          .load(`${PREVIEW_INK_REF_SIZE}px '${family}'`, span.textContent || 'A')
          // rAF: Canvas kennt die Glyphen sofort, das Layout erst im naechsten
          // Frame — sonst passt die Groesse kurz nicht zum gezeigten Bild.
          .then(() => window.requestAnimationFrame(() => this.#fitPreviewSpan(span)))
          .catch(() => {});
      }
    }

    this.#fitPreviewSpan(span);
  }

  #fitVisiblePreviewSpan() {
    for (const span of [this.refs.previewText, this.refs.previewGhost]) {
      if (span && !span.hidden) {
        this.#fitPreviewSpan(span);
      }
    }
  }

  /**
   * Passt die Schriftgroesse ein, sodass die INK des Schriftzugs — die
   * tatsaechlich bedruckte Flaeche, nicht die Layout-Box — den Gravurbereich
   * nicht ueberlaeuft (AK 27), und zentriert sie darin (BUG-0002).
   *
   * Vorher wurde die Layout-Box gemessen (scrollWidth/scrollHeight bzw. die
   * Union der Zeichen-Rects). Das ist die Advance-Breite und die Line-Box;
   * Script-Fonts ragen mit ihren Schwuengen weit darueber hinaus. Gemessen
   * an "TF" in Great Vibes bei 96px: Layout 190,4x105,6 gegenueber einer Ink
   * von 223,1x81,0 in einer Flaeche von 218,8x158,8 — 21px liefen rechts aus
   * der Form und wurden vom overflow: hidden abgeschnitten.
   */
  #fitPreviewSpan(span) {
    const shape = this.refs.previewShape;

    if (!shape || !span || span.hidden || span.textContent === '') {
      return;
    }

    const computed = window.getComputedStyle(shape);
    const bottomReserve = this.#previewBottomReserve();

    const maxWidth =
      shape.clientWidth -
      parseFloat(computed.paddingLeft) -
      parseFloat(computed.paddingRight) -
      2 * PREVIEW_INK_RELIEF_PAD;

    // Der Puffer fuer die Overlay-Blaetterpfeile (Smoke-Befund T4) wird nur
    // noch EINFACH abgezogen, nicht mehr doppelt: die Ink wird jetzt gezielt
    // platziert (#applyInkShift schiebt sie um reserve/2 nach oben), statt
    // sich auf die symmetrische Flex-Zentrierung zu verlassen. Das gibt dem
    // Versatz-Design die Haelfte des Puffers als Schriftgroesse zurueck.
    const fullHeight = shape.clientHeight - parseFloat(computed.paddingTop) - parseFloat(computed.paddingBottom);
    const maxHeight = fullHeight - bottomReserve - 2 * PREVIEW_INK_RELIEF_PAD;

    if (maxWidth <= 0 || maxHeight <= 0) {
      return;
    }

    const ink = this.#measureInk(span);

    // Kein Canvas, keine TextMetrics-Details oder Webfont noch nicht geladen:
    // bisheriges Verhalten, damit alte Browser keine Regression sehen. Der
    // Font-Load stoesst spaeter ein erneutes Einpassen an.
    if (!ink) {
      this.#clearInkShift(span);
      this.#clearInkPadding(span);
      this.#fitPreviewSpanByLayout(span, maxWidth, fullHeight - 2 * bottomReserve);
      return;
    }

    const inkWidth = ink.x1 - ink.x0;
    const inkHeight = ink.y1 - ink.y0;

    if (inkWidth <= 0 || inkHeight <= 0) {
      this.#clearInkShift(span);
      this.#clearInkPadding(span);
      return;
    }

    // Die Ink skaliert linear mit der Schriftgroesse — ein Dreisatz ersetzt
    // die fruehere Binaersuche und damit sieben erzwungene Layout-Zyklen pro
    // Tastendruck. Math.floor macht das Ergebnis garantiert nicht zu gross.
    const fitted = PREVIEW_INK_REF_SIZE * Math.min(maxWidth / inkWidth, maxHeight / inkHeight);
    const size = Math.max(PREVIEW_MIN_FONT_SIZE, Math.min(PREVIEW_MAX_FONT_SIZE, Math.floor(fitted)));

    span.style.fontSize = `${size}px`;
    this.#applyInkPadding(span, ink);
    this.#applyInkShift(span, ink, size, bottomReserve, fullHeight);
  }

  /**
   * Vergroessert die Textbox um den Ueberhang der Glyphen (BUG-0002).
   *
   * Der eigentliche Beschnitt entsteht NICHT am Rand der Gravurflaeche,
   * sondern an der Box des Schriftzugs selbst: Die Gravurfarbe ist ein
   * Verlauf, der per background-clip: text durch die Buchstaben ausgestanzt
   * wird (FEATURE-0008), und ein Hintergrund reicht nur bis zur Padding-Box.
   * Da der Text zugleich color: transparent traegt, ist jeder Teil einer
   * Glyphe ausserhalb dieser Box schlicht unsichtbar — bei "TF" in Great
   * Vibes 33,6px des F, das dadurch senkrecht abgeschnitten aussieht.
   *
   * Script-Fonts ragen genau so aus ihrer Layout-Box heraus: seitlich ueber
   * die Advance-Breite (Schwuenge), oben und unten ueber die Line-Box
   * (line-height 1.1 ist kleiner als Ascent + Descent dieser Schriften).
   *
   * Das Padding wird deshalb exakt auf den gemessenen Ueberhang gesetzt und
   * mit einem negativen Margin gleicher Groesse ausgeglichen — die Box waechst
   * nur fuer den Farbverlauf, das Layout bleibt unveraendert. In em, damit es
   * mit der eingepassten Schriftgroesse skaliert. Im Versatz-Design tragen die
   * Zeichen-Spans denselben Wert; sie sind eigene Boxen mit eigenem Verlauf.
   */
  #applyInkPadding(span, ink) {
    // Kleine Zugabe auf jeder Seite: deckt das Gravur-Relief (drop-shadow,
    // liegt ausserhalb der Glyphe) und die Rundungsdifferenz zwischen
    // Canvas-Messung und gerendertem Layout ab.
    const ratio = (value) =>
      `${(Math.max(0, value) / PREVIEW_INK_REF_SIZE + PREVIEW_INK_PAD_SAFETY_EM).toFixed(4)}em`;

    // Gemessen wird gegen die Layout-Box des Containers: horizontal die
    // Advance-Breite, vertikal die Line-Box. Die Ink-Union enthaelt im
    // Versatz-Pfad bereits die Zeichen-Offsets — deren Ueberhang deckt das
    // Padding damit mit ab, seit die Zeichen ihre Farbe vom Container beziehen.
    span.style.setProperty('--cm-ink-pad-top', ratio(-(ink.baselineFromTop + ink.y0)));
    span.style.setProperty('--cm-ink-pad-bottom', ratio(ink.baselineFromTop + ink.y1 - ink.boxHeight));
    span.style.setProperty('--cm-ink-pad-left', ratio(-ink.x0));
    span.style.setProperty('--cm-ink-pad-right', ratio(ink.x1 - ink.advance));

    // Die Zeichen-Spans des Versatz-Designs faerben sich selbst und brauchen
    // deshalb ihre eigene, kleinere Farbbox: gemessen gegen die Advance-Box
    // des einzelnen Zeichens, nicht gegen die Layout-Box des ganzen Wortes.
    if (!ink.eigen) {
      for (const name of ['--cm-char-pad-top', '--cm-char-pad-right', '--cm-char-pad-bottom', '--cm-char-pad-left']) {
        span.style.removeProperty(name);
      }

      return;
    }

    span.style.setProperty('--cm-char-pad-top', ratio(ink.eigen.ascent - ink.baselineFromTop));
    span.style.setProperty('--cm-char-pad-bottom', ratio(ink.eigen.descent - (ink.boxHeight - ink.baselineFromTop)));
    span.style.setProperty('--cm-char-pad-left', ratio(ink.eigen.left));
    span.style.setProperty('--cm-char-pad-right', ratio(ink.eigen.right));
  }

  #clearInkPadding(span) {
    for (const name of [
      '--cm-ink-pad-top', '--cm-ink-pad-right', '--cm-ink-pad-bottom', '--cm-ink-pad-left',
      '--cm-char-pad-top', '--cm-char-pad-right', '--cm-char-pad-bottom', '--cm-char-pad-left',
    ]) {
      span.style.removeProperty(name);
    }
  }

  /**
   * Bisheriger Einpass-Weg per Binaersuche ueber die Layout-Metriken.
   * Faellt nur noch ein, wenn die Ink-Messung nicht moeglich ist.
   */
  #fitPreviewSpanByLayout(span, maxWidth, maxHeight) {
    if (maxWidth <= 0 || maxHeight <= 0) {
      return;
    }

    let low = PREVIEW_MIN_FONT_SIZE;
    let high = PREVIEW_MAX_FONT_SIZE;

    while (low < high) {
      const mid = Math.ceil((low + high) / 2);
      span.style.fontSize = `${mid}px`;
      const size = this.#measurePreviewSpan(span);

      if (size.width <= maxWidth && size.height <= maxHeight) {
        low = mid;
      } else {
        high = mid - 1;
      }
    }

    span.style.fontSize = `${low}px`;
  }

  /**
   * Schiebt den Schriftzug so, dass die INK mittig in der Gravurflaeche sitzt.
   *
   * Verkleinern allein reicht nicht: die Flexbox zentriert die Layout-Box,
   * und die Ink sitzt bei Script-Fonts asymmetrisch darin. Bei "TF" in Great
   * Vibes blieben links 16,7px frei, waehrend rechts 21px abgeschnitten
   * wurden — ohne Verschiebung muesste die Schrift um diesen Fehlbetrag
   * kleiner werden und saesse trotzdem schief.
   */
  #applyInkShift(span, ink, size, bottomReserve, availableHeight) {
    const scale = size / PREVIEW_INK_REF_SIZE;

    // Was die Flexbox tatsaechlich zentriert: horizontal die Advance-Breite
    // (der Ink-Ueberhang veraendert die Box-Breite nicht), vertikal die
    // Line-Box.
    const layoutWidth = ink.advance * scale;
    const layoutHeight = ink.boxHeight * scale;

    const inkCenterX = ((ink.x0 + ink.x1) / 2) * scale;
    const inkCenterY = (ink.baselineFromTop + (ink.y0 + ink.y1) / 2) * scale;

    // bottomReserve haelt die Blaetterpfeile frei. Der Schriftzug wird dafuer
    // nur so weit angehoben, wie er die Pfeilzone tatsaechlich beruehrt —
    // ein pauschaler Versatz um reserve/2 wuerde ihn an die Oberkante
    // druecken (gemessen: 4px Luft oben gegen 31px unten). Bleibt bei
    // mittiger Lage ohnehin genug Raum, ist die Hebung 0.
    const freeBelowWhenCentered = (availableHeight - (ink.y1 - ink.y0) * scale) / 2;
    const lift = Math.max(0, bottomReserve - freeBelowWhenCentered);

    this.#setInkShift(span, layoutWidth / 2 - inkCenterX, layoutHeight / 2 - inkCenterY - lift);
  }

  /**
   * Verschiebung als Custom Property; die Technik (transform) steht im CSS.
   * Auf Geraetepixel gerundet, weil eine fraktionale Translation auf dem
   * filter-Layer des Reliefs die Glyphen weich zeichnen wuerde.
   */
  #setInkShift(span, dx, dy) {
    const dpr = window.devicePixelRatio || 1;

    span.style.setProperty('--cm-ink-dx', `${Math.round(dx * dpr) / dpr}px`);
    span.style.setProperty('--cm-ink-dy', `${Math.round(dy * dpr) / dpr}px`);
  }

  #clearInkShift(span) {
    span.style.removeProperty('--cm-ink-dx');
    span.style.removeProperty('--cm-ink-dy');
  }

  /**
   * Unterer Hoehen-Puffer fuer die Overlay-Blaetterpfeile (FEATURE-0003,
   * Smoke-Befund T4): Die Pfeile ragen per translateY(50%) zur Haelfte
   * (halber Button-Durchmesser, 22px) in die Shape und wuerden sonst den
   * Schriftzug ueberlappen. Seit BUG-0002 wird der Wert einfach statt doppelt
   * abgezogen und der Schriftzug nur so weit angehoben, wie er die Pfeilzone
   * wirklich beruehrt — das haelt denselben Raum frei und gibt den Rest als
   * Schriftgroesse zurueck.
   *
   * Der Puffer gilt fuer JEDEN Textpfad, sobald die Buttons auf der Vorschau
   * liegen (Picker sichtbar) — frueher nur fuer den Versatz-Pfad, weil das
   * Groessenlimit von 96px den Standard-Pfad nie so gross werden liess. Mit
   * dem angehobenen Limit fuellt etwa "TF" in Cinzel die Flaeche bis auf
   * wenige Pixel aus und die Pfeile laegen mitten auf den Buchstaben.
   * Ghost-Initialen und Nicht-Text-Modi bleiben unberuehrt: dort ist der
   * Picker ohnehin ausgeblendet.
   */
  #previewBottomReserve() {
    const picker = this.refs.designPicker;

    if (!picker || picker.hidden) {
      return 0;
    }

    // offsetHeight statt CSS-Konstante: folgt der tatsaechlichen
    // Button-Groesse (--clubmonee-engraving-design-arrow-size).
    const buttonHeight = this.refs.designPrev?.offsetHeight ?? 0;
    return buttonHeight / 2 + PREVIEW_DESIGN_BUTTON_GAP;
  }

  /**
   * Misst die LAYOUT-Bounding-Box des Vorschau-Inhalts. Seit BUG-0002 nur
   * noch Fallback fuer #fitPreviewSpanByLayout, wenn die Ink-Messung nicht
   * moeglich ist (kein Canvas, keine TextMetrics-Details, Webfont fehlt).
   *
   * Der Versatz laeuft ueber transform: translateY, das die Layout-Metriken
   * (scrollWidth/scrollHeight) NICHT veraendert — daher Union der
   * getBoundingClientRect der Zeichen-Spans (enthaelt Transforms). Ohne
   * Zeichen-Spans (Ghost-Initialen) genuegen die Layout-Metriken.
   */
  #measurePreviewSpan(span) {
    const chars = span.querySelectorAll('[data-clubmonee-design-char]');

    if (chars.length === 0) {
      return { width: span.scrollWidth, height: span.scrollHeight };
    }

    let top = Infinity;
    let bottom = -Infinity;
    let left = Infinity;
    let right = -Infinity;

    for (const char of chars) {
      const rect = char.getBoundingClientRect();
      top = Math.min(top, rect.top);
      bottom = Math.max(bottom, rect.bottom);
      left = Math.min(left, rect.left);
      right = Math.max(right, rect.right);
    }

    return { width: right - left, height: bottom - top };
  }

  /**
   * Misst die INK-Box des Vorschautexts per Canvas TextMetrics (BUG-0002) —
   * die tatsaechlich bedruckte Flaeche, nicht die Advance-/Line-Box.
   *
   * Gemessen wird IMMER bei PREVIEW_INK_REF_SIZE; Ink, Advance und die
   * em-basierten Design-Offsets skalieren linear mit der Schriftgroesse.
   *
   * Rueckgabe in CSS-px bei Referenzgroesse, Ursprung ist der Textanfang auf
   * der Grundlinie: { x0, x1, y0, y1, advance, baselineFromTop, boxHeight }.
   * null bedeutet "nicht messbar" — dann greift der Layout-Pfad wie bisher.
   */
  #measureInk(span) {
    const ctx = inkContext();

    if (!ctx) {
      return null;
    }

    const cs = window.getComputedStyle(span);
    const currentSize = parseFloat(cs.fontSize);

    // Solange der Webfont nicht da ist (font-display: swap zeigt zuerst den
    // Systemfont), misst Canvas eine voellig andere Schrift. Dann lieber gar
    // nicht messen — #applyPreviewFont passt nach dem Laden erneut ein.
    if (!this.#previewFontReady(primaryFamily(cs.fontFamily), span.textContent)) {
      return null;
    }

    // Font-Shorthand aus dem Element uebernehmen. Bei ungueltigem Shorthand
    // ist die Zuweisung ein No-op — deshalb der Sentinel davor.
    ctx.font = '10px sans-serif';
    ctx.font = `${cs.fontStyle} ${cs.fontWeight} ${PREVIEW_INK_REF_SIZE}px ${cs.fontFamily}`;

    if (ctx.font === '10px sans-serif') {
      return null;
    }

    // letter-spacing erbt der Span vom Theme (aktuell -0.01em) und ist
    // em-relativ, muss bei Referenzgroesse also neu ausgerechnet werden.
    const spacing = emRatio(cs.letterSpacing, currentSize) * PREVIEW_INK_REF_SIZE;
    const hasSpacingApi = 'letterSpacing' in ctx;

    if (hasSpacingApi) {
      ctx.letterSpacing = `${spacing}px`;
    }

    const chars = span.querySelectorAll('[data-clubmonee-design-char]');
    const spacingFallback = hasSpacingApi ? 0 : spacing;
    const box = chars.length
      ? this.#measureInkChars(ctx, chars, cs, spacingFallback)
      : this.#measureInkRun(ctx, applyTextTransform(span.textContent, cs.textTransform), spacingFallback);

    if (!box) {
      return null;
    }

    // Lage der Grundlinie in der Layout-Box (halbes Leading + Font-Ascent).
    // Ohne diesen Bezug laesst sich die Ink nicht gegen die Flex-Zentrierung
    // der Layout-Box verrechnen.
    const probe = ctx.measureText('M');

    if (typeof probe.fontBoundingBoxAscent !== 'number') {
      return null;
    }

    const contentHeight = probe.fontBoundingBoxAscent + probe.fontBoundingBoxDescent;
    const lineHeight =
      cs.lineHeight === 'normal' ? contentHeight : emRatio(cs.lineHeight, currentSize) * PREVIEW_INK_REF_SIZE;

    box.baselineFromTop = (lineHeight - contentHeight) / 2 + probe.fontBoundingBoxAscent;
    box.boxHeight = lineHeight;

    return box;
  }

  /**
   * Standard-Pfad: ein einzelner Text-Node. Der ganze String geht in EINE
   * Messung — nur so bleiben Kerning und Ligaturen der Script-Fonts erhalten,
   * genau wie das DOM sie rendert (siehe #renderPreviewChars).
   */
  #measureInkRun(ctx, text, spacingFallback) {
    if (text === '') {
      return null;
    }

    const metrics = ctx.measureText(text);

    if (typeof metrics.actualBoundingBoxAscent !== 'number') {
      return null;
    }

    // Ohne ctx.letterSpacing: Glyphe k sitzt um k * spacing weiter rechts,
    // die Advance waechst um n * spacing (auch nach dem letzten Zeichen —
    // so rechnet das Inline-Layout ebenfalls).
    const count = spacingFallback === 0 ? 0 : [...text].length;

    return {
      x0: -metrics.actualBoundingBoxLeft,
      x1: metrics.actualBoundingBoxRight + Math.max(0, count - 1) * spacingFallback,
      y0: -metrics.actualBoundingBoxAscent,
      y1: metrics.actualBoundingBoxDescent,
      advance: metrics.width + count * spacingFallback,
    };
  }

  /**
   * Versatz-Design-Pfad: jedes Zeichen ist ein eigenes inline-block. Dadurch
   * faellt das Kerning zwischen den Zeichen weg und jede Box ist genau eine
   * Advance breit — die Pen-Positionen sind hier also die Summe der
   * Einzel-Advances, nicht das Shaping des Gesamtstrings. Der vertikale
   * Versatz steht als --cm-offset (em) am Zeichen-Span (CSS: position
   * relative/top) und geht direkt in die Union ein.
   */
  #measureInkChars(ctx, chars, cs, spacingFallback) {
    let pen = 0;
    let x0 = Infinity;
    let x1 = -Infinity;
    let y0 = Infinity;
    let y1 = -Infinity;
    // Ueberhang je Zeichen gegen die EIGENE Advance-Box: jedes Zeichen traegt
    // seinen Farbverlauf selbst (WebKit erfasst Kind-Glyphen nicht im Clip des
    // Containers), also braucht jedes auch seine eigene Farbbox.
    const eigen = { left: 0, right: 0, ascent: 0, descent: 0 };

    for (const char of chars) {
      const text = applyTextTransform(char.textContent, cs.textTransform);
      const metrics = ctx.measureText(text);

      if (typeof metrics.actualBoundingBoxAscent !== 'number') {
        return null;
      }

      // Leerzeichen haben keine Ink und duerfen die Union nicht aufziehen;
      // sie zaehlen nur mit ihrer Advance (dort setzt das JS keinen Offset).
      if (text.trim() !== '') {
        const offset = parseFloat(char.style.getPropertyValue('--cm-offset')) || 0;
        const dy = offset * PREVIEW_INK_REF_SIZE;

        x0 = Math.min(x0, pen - metrics.actualBoundingBoxLeft);
        x1 = Math.max(x1, pen + metrics.actualBoundingBoxRight);
        y0 = Math.min(y0, dy - metrics.actualBoundingBoxAscent);
        y1 = Math.max(y1, dy + metrics.actualBoundingBoxDescent);

        eigen.left = Math.max(eigen.left, metrics.actualBoundingBoxLeft);
        eigen.right = Math.max(eigen.right, metrics.actualBoundingBoxRight - metrics.width);
        eigen.ascent = Math.max(eigen.ascent, metrics.actualBoundingBoxAscent);
        eigen.descent = Math.max(eigen.descent, metrics.actualBoundingBoxDescent);
      }

      pen += metrics.width + spacingFallback;
    }

    return x0 === Infinity ? null : { x0, x1, y0, y1, advance: pen, eigen };
  }

  /**
   * Ist der Webfont fuer genau diesen Text einsatzbereit? Der Text gehoert
   * mit in die Pruefung: bei unicode-range-Subsets waere check() sonst
   * falsch-negativ fuer die tatsaechlich eingegebenen Zeichen.
   */
  #previewFontReady(family, text) {
    if (!family || !document.fonts?.check) {
      return false;
    }

    try {
      // check() liefert auch true, wenn es fuer die Family gar keine
      // @font-face gibt (Systemfont) — dann ist die Messung ebenfalls korrekt.
      return document.fonts.check(`${PREVIEW_INK_REF_SIZE}px '${family}'`, text || 'A');
    } catch {
      return false;
    }
  }

  /**
   * Kauf-Wege, die den Submit-Intercept umgehen wuerden, ausblenden, sobald
   * die Gravur aktiv ist (Inhalt im aktiven Modus vorhanden oder Upload laeuft):
   * - Sticky-ATC (AK 70/71)
   * - Dynamic-Checkout-/"Buy it now"-Button (Review-Finding 1): der Payment-
   *   Button feuert kein submit-Event, sondern geht direkt in den Checkout —
   *   Gravur und Gebuehr wuerden komplett verloren gehen.
   * Die Klasse liegt auf dem stabilen Section-Root (nicht auf den Buttons),
   * weil der buy_buttons-Block beim Variantenwechsel partial-rerendered wird
   * und eine Button-Klasse dabei verloren ginge; das CSS blendet beide
   * Elemente per Descendant-Selektor aus (assets/clubmonee-overrides.css).
   */
  #updateBypassButtons() {
    const active = this.#hasEngravingContent() || this.state.uploading;
    const sectionRoot = this.closest('.shopify-section');
    sectionRoot?.classList.toggle('clubmonee-engraving-active', active);
  }

  /**
   * mailto-Betreff des Gravurwunsch-Links auf die SKU der gewaehlten Variante setzen.
   * Der Link wird serverseitig bereits korrekt gerendert — hier wird nur nachgezogen,
   * wenn der Kunde die Variante wechselt (Legierung/Ringweite aendern die SKU).
   * Ohne SKU (Variante ohne Artikelnummer) bleibt der Link unveraendert, statt einen
   * Betreff mit leerer oder veralteter Artikelnummer zu erzeugen.
   */
  #updateCustomRequestLink(sku) {
    const link = this.refs.customRequest;
    const template = this.i18n.customRequestSubject;

    if (!link || !template || !sku) {
      return;
    }

    const [mailto] = link.href.split('?');
    const subject = template.replace('[sku]', sku).trim();

    link.href = `${mailto}?subject=${encodeURIComponent(subject)}`;
  }

  #showError(code) {
    if (!this.refs.error) {
      return;
    }

    this.refs.error.textContent = this.i18n.errors?.[code] ?? this.i18n.errors?.generic ?? '';
    this.refs.error.hidden = false;
  }

  #clearError() {
    if (this.refs.error) {
      this.refs.error.hidden = true;
      this.refs.error.textContent = '';
    }
  }

  /* ------------------------------------------------------------------ */
  /* Formular-Helfer                                                     */
  /* ------------------------------------------------------------------ */

  #productForm() {
    const formId = this.getAttribute('form');
    return formId ? document.getElementById(formId) : null;
  }

  #submitButtons(form) {
    return Array.from(form?.elements ?? []).filter((element) => element.type === 'submit');
  }

  /**
   * ATC nur waehrend eines laufenden Uploads sperren (AK 21/23).
   * Merkt sich, welche Buttons WIR gesperrt haben, damit z. B. ein vom
   * Theme gesperrter Sold-out-Button nicht faelschlich freigegeben wird.
   */
  #setAddToCartDisabled(disabled) {
    if (disabled) {
      this.lockedButtons = this.#submitButtons(this.#productForm()).filter((button) => !button.disabled);

      for (const button of this.lockedButtons) {
        button.setAttribute('disabled', '');
      }
    } else {
      for (const button of this.lockedButtons ?? []) {
        button.removeAttribute('disabled');
      }

      this.lockedButtons = [];
    }
  }

  /* ------------------------------------------------------------------ */
  /* Entwurfs-Persistenz (AK 59-61)                                      */
  /* ------------------------------------------------------------------ */

  #draftKey() {
    return `${DRAFT_KEY_PREFIX}${this.dataset.productHandle ?? ''}`;
  }

  #saveDraft() {
    safeLocalStorage.set(this.#draftKey(), {
      mode: this.state.mode,
      text: this.state.text,
      font: this.state.font,
      designKey: this.state.designKey,
      imageUrl: this.state.imageUrl,
      uploadId: this.state.uploadId,
      fileName: this.state.fileName,
      iconKey: this.state.iconKey,
      articleByMode: this.state.articleByMode,
      gravurId: this.state.clientGravurId,
    });
  }

  #restoreDraft() {
    const draft = safeLocalStorage.get(this.#draftKey());

    if (!draft || typeof draft !== 'object') {
      return;
    }

    // Migration: alte Drafts (Checkbox-Aera, erkennbar am 'checked'-Feld)
    // werden verworfen — das Format ist nicht mehr kompatibel.
    if ('checked' in draft) {
      this.#clearDraft();
      return;
    }

    this.state.mode = MODES.includes(draft.mode) ? draft.mode : 'none';

    // Modus nur wiederherstellen, wenn er auf diesem Produkt auch angeboten wird
    // (Metafeld-Verknuepfungen koennen sich geaendert haben). Sonst neutral 'none'.
    if (this.state.mode !== 'none' && !this.refs.modeRadios.some((radio) => radio.value === this.state.mode)) {
      this.state.mode = 'none';
    }

    this.state.text = normalizeEngravingText(draft.text);
    this.state.imageUrl = draft.imageUrl ?? null;
    this.state.uploadId = draft.uploadId ?? null;
    this.state.fileName = draft.fileName ?? null;
    this.state.iconKey = typeof draft.iconKey === 'string' ? draft.iconKey : null;
    this.state.clientGravurId = draft.gravurId ?? null;

    // Design-Wahl (FEATURE-0003): nur bekannte Keys uebernehmen, sonst
    // 'standard'. Alte Drafts ohne designKey laden ohne Migration (Feld
    // fehlt → 'standard'). Ob die Wahl WIRKSAM ist, entscheidet allein die
    // Text-Eligibility in #effectiveDesignKey() — die gespeicherte Wahl
    // bleibt fuer die Rueckkehr ins 2-3-Zeichen-Fenster erhalten.
    this.state.designKey = DESIGN_KEYS.includes(draft.designKey) ? draft.designKey : DEFAULT_DESIGN_KEY;

    // Gewaehlte Gravurartikel je Modus wiederherstellen; nicht mehr verknuepfte
    // IDs fallen auf den ersten verfuegbaren Artikel des Modus zurueck.
    const restoredArticles = this.#defaultArticleByMode();

    if (draft.articleByMode && typeof draft.articleByMode === 'object') {
      for (const mode of ARTICLE_MODES) {
        const stored = Number(draft.articleByMode[mode]);

        if (stored && this.articles[mode].some((article) => article.id === stored)) {
          restoredArticles[mode] = stored;
        }
      }
    }

    this.state.articleByMode = restoredArticles;

    // Nur bekannte Icon-Keys wiederherstellen
    if (this.state.iconKey) {
      const known = this.refs.iconButtons.some((button) => button.dataset.clubmoneeIcon === this.state.iconKey);

      if (!known) {
        this.state.iconKey = null;
      }
    }

    for (const radio of this.refs.modeRadios) {
      radio.checked = radio.value === this.state.mode;
    }

    this.#syncArticlePickers();

    if (this.refs.textInput) {
      this.refs.textInput.value = this.state.text;
    }

    if (typeof draft.font === 'string' && this.refs.fontSelect) {
      const hasOption = Array.from(this.refs.fontSelect.options).some((option) => option.value === draft.font);

      if (hasOption) {
        this.refs.fontSelect.value = draft.font;
        this.state.font = draft.font;
      }
    }
  }

  #clearDraft() {
    safeLocalStorage.remove(this.#draftKey());
  }

  #resetAfterAdd() {
    this.state.mode = 'none';
    this.state.text = '';
    this.state.designKey = DEFAULT_DESIGN_KEY;
    this.state.imageUrl = null;
    this.state.uploadId = null;
    this.state.fileName = null;
    this.state.iconKey = null;
    this.state.articleByMode = this.#defaultArticleByMode();
    this.state.clientGravurId = null;

    for (const radio of this.refs.modeRadios) {
      radio.checked = radio.value === 'none';
    }

    this.#syncArticlePickers();

    if (this.refs.textInput) {
      this.refs.textInput.value = '';
    }

    if (this.refs.fileInput) {
      this.refs.fileInput.value = '';
    }

    this.#clearError();
    this.#render();
  }

  /* ------------------------------------------------------------------ */
  /* Getter                                                              */
  /* ------------------------------------------------------------------ */

  #maxChars() {
    return Number(this.dataset.maxChars) || 10;
  }

  #maxFileMb() {
    return Number(this.dataset.maxFileMb) || 10;
  }
}

if (!window.customElements.get('clubmonee-engraving')) {
  window.customElements.define('clubmonee-engraving', ClubmoneeEngraving);
}
